import React, { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FaExclamationCircle } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import FitGuide from '~/acquisition/components/FitGuide';
import AlternateSizeGuide from '~/shared/components/AlternateSizeGuide';
import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import { Button, desktop, mobile } from '~/techstyle-shared/react-components';
import { useIntl, FormattedMessage } from '~/techstyle-shared/react-intl';

const FitGuideLink = styled(Button).attrs({
  variant: 'unstyled',
})`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  margin: ${({ theme }) => theme.selectorStyles.sizeGuideMargin};
  & > span {
    color: ${({ theme }) => theme.colors.fitGuideLink};
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.025em;
    text-decoration: underline;
    gap: 4px;
    margin-right: 4px;
  }
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 18px;
`;

const ExclamationIcon = styled(FaExclamationCircle)`
  color: ${({ theme }) => theme.colors.fitGuideExclamationIcon};
  height: 12px;
  width: 12px;
`;

const modalContentWrapperStyles = css`
  // The FableticsModal makes this a flex display, which causes an extraneous horizontal scrollbar
  // to appear.
  display: unset;
  overflow: hidden;
`;

const dialogStyle = css`
  ${desktop`
		min-height: 600px;
    max-height: calc(100vh - 128px);
		min-width: 948px;
		overflow: hidden;
		:hover {
			overflow-y: overlay;
		}
  `}
  ${mobile`
		height: 100vh;
		width: 100vw;
		::-webkit-scrollbar {
			display: none;
		}
		-ms-overflow-style: none;
  	scrollbar-width: none;
	`}
`;

const alternateDialogStyle = css`
  ${dialogStyle}

  ${desktop`
		min-width: unset;
  `}
`;

const transitionProps = {
  enter: { opacity: 1, transform: 'scale(1, 1)' },
  from: { opacity: 0, transform: 'scale(0.5, 0.5)' },
  leave: { opacity: 0, transform: 'scale(0.5, 0.5)' },
};

const SizeFabricGuideModal = ({ useAlternate }) => {
  /* TODO: add modal for the new fitguide once the designs are ready */

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const intl = useIntl();
  const closeLabel = intl.formatMessage({
    id: 'global_cta.close',
    defaultMessage: 'Close',
  });

  const dialogStyleToUse = useAlternate ? alternateDialogStyle : dialogStyle;

  return (
    <>
      <FitGuideLink data-autotag="pdp-size-guide-link" onClick={openModal}>
        <FormattedMessage
          id="site_pdp.guides"
          defaultMessage="Size and Fabric Guides"
        />
        <ExclamationIcon />
      </FitGuideLink>
      <FableticsModal
        contentWrapperStyles={modalContentWrapperStyles}
        dialogStyle={dialogStyleToUse}
        isOpen={isModalOpen}
        onExit={closeModal}
        closeButton={<ModalCloseButton aria-label={closeLabel} />}
        showCloseButton
        transitionEnter={transitionProps.enter}
        transitionFrom={transitionProps.from}
        transitionLeave={transitionProps.leave}
        title={intl.formatMessage({
          id: 'site_pdp.guides',
          defaultMessage: 'Size and Fabric Guides',
        })}
        variant="unpadded"
      >
        {useAlternate ? <AlternateSizeGuide /> : <FitGuide />}
      </FableticsModal>
    </>
  );
};

SizeFabricGuideModal.propTypes = {
  useAlternate: PropTypes.bool, // use the alternate size guide for this product
};

export default SizeFabricGuideModal;
